/**
 * ManageCollectionsDialog Component
 *
 * A modal dialog that manages which of the current user's collections a single
 * journal article (source) belongs to. The user ticks/unticks collections and
 * saves;
 */

import { useEffect, useMemo, useRef, useState, type JSX } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Loader2 } from "lucide-react"
import MultiSelectCombobox, {
  ComboboxOption,
} from "@/components/shared/MultiSelectCombobox"
import {
  BiblioCollection,
  USER_COLLECTION_TYPE,
  isCollectionMember,
  addSourcesToCollection,
  removeSourcesFromCollection,
} from "@/services/biblioService"
import { useAuth } from "@/hooks/useAuth"

/** The outcome of a successful save, passed back to the parent. */
export interface ManageCollectionsResult {
  /** The source id whose memberships were changed. */
  sourceId: number
  /** Collection ids the article was newly added to. */
  addedCollectionIds: number[]
  /** Collection ids the article was removed from. */
  removedCollectionIds: number[]
}

/** Props for the ManageCollectionsDialog component. */
interface ManageCollectionsDialogProps {
  /** Whether the dialog is open. */
  open: boolean
  /** The source id of the article whose collections are being managed. */
  sourceId: number
  /**
   * All collections currently loaded for the user. The dialog filters this
   * list down to the user collections the user may manage (see component docs).
   */
  collections: BiblioCollection[]
  /** The collection ids the article currently belongs to. */
  existingCollectionIds: number[]
  /** Called to request closing the dialog (Cancel, overlay, or Escape). */
  onClose: () => void
  /** Called with the change outcome after a successful save. */
  onChanged: (result: ManageCollectionsResult) => void
  /**
   * Optional callback invoked when the user clicks "Create a new collection"
   * in the empty state.
   */
  onRequestCreateCollection?: () => void
}

/**
 * Modal dialog for managing which of the user's collections an article is in.
 *
 * @param props - Component props.
 * @param props.open - Whether the dialog is open.
 * @param props.sourceId - The source id whose collections are being managed.
 * @param props.collections - All loaded collections, filtered internally.
 * @param props.existingCollectionIds - Collection ids the article is currently in.
 * @param props.onClose - Handler to close the dialog.
 * @param props.onChanged - Handler invoked with the change outcome on success.
 * @returns The rendered dialog.
 */
export default function ManageCollectionsDialog({
  open,
  sourceId,
  collections,
  existingCollectionIds,
  onClose,
  onChanged,
  onRequestCreateCollection,
}: ManageCollectionsDialogProps): JSX.Element {
  const { getAccessToken, user } = useAuth()

  /** The current user's Auth0 "sub", used for the membership check. */
  const currentUserSub: string = user?.sub ?? ""

  /**
   * Ref to the dialog's content element. The combobox dropdown is portaled into
   * this element so the modal dialog does not block interaction with it.
   */
  const dialogContentRef = useRef<HTMLDivElement | null>(null)

  /** The collection ids currently ticked in the picker (as strings). */
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  /** Whether a save request is in flight. */
  const [saving, setSaving] = useState<boolean>(false)
  /** Error message shown when the save request fails. */
  const [saveError, setSaveError] = useState<string | null>(null)

  /**
   * The user collections the current user can manage for this article:
   * user-created collections the user is an explicit member of. Public and
   * topic collections are excluded — this dialog only governs the user's own
   * collections. Both add and remove candidates come from this one set.
   */
  const manageableCollections = useMemo<BiblioCollection[]>(
    () =>
      collections.filter(
        (collection) =>
          collection.collectionType === USER_COLLECTION_TYPE &&
          isCollectionMember(collection, currentUserSub)
      ),
    [collections, currentUserSub]
  )

  /** Combobox options derived from the manageable collections. */
  const options = useMemo<ComboboxOption[]>(
    () =>
      manageableCollections.map((collection) => ({
        value: String(collection.id),
        label: collection.collectionName,
      })),
    [manageableCollections]
  )

  /**
   * The baseline selection: the manageable collections the article is already
   * in.
   */
  const baselineSelectedIds = useMemo<string[]>(() => {
    const manageableIds = new Set<number>(
      manageableCollections.map((collection) => collection.id)
    )
    return existingCollectionIds
      .filter((id) => manageableIds.has(id))
      .map((id) => String(id))
  }, [manageableCollections, existingCollectionIds])

  // Seed the picker from the baseline each time the dialog opens (or the
  // underlying data changes), and clear any stale error.
  useEffect(() => {
    if (!open) {
      return
    }
    setSelectedIds(baselineSelectedIds)
    setSaveError(null)
  }, [open, baselineSelectedIds])

  /** Whether the user has no manageable collections at all. */
  const noOptions: boolean = options.length === 0

  /**
   * The pending changes: ids newly ticked (to add) and ids unticked (to
   * remove), computed against the baseline selection.
   */
  const { toAdd, toRemove } = useMemo<{
    toAdd: number[]
    toRemove: number[]
  }>(() => {
    const baseline = new Set<string>(baselineSelectedIds)
    const selected = new Set<string>(selectedIds)
    const added: number[] = selectedIds
      .filter((id) => !baseline.has(id))
      .map((id) => Number(id))
    const removed: number[] = baselineSelectedIds
      .filter((id) => !selected.has(id))
      .map((id) => Number(id))
    return { toAdd: added, toRemove: removed }
  }, [baselineSelectedIds, selectedIds])

  /** Whether there is any pending change to save. */
  const hasChanges: boolean = toAdd.length > 0 || toRemove.length > 0

  /**
   * Request the create-collection flow owned by the parent (the sidebar's New
   * Collection dialog), then close this dialog so the sidebar popup is not
   * obscured. No-op if the parent did not supply a handler.
   */
  const handleRequestCreateCollection = (): void => {
    onRequestCreateCollection?.()
    onClose()
  }

  /**
   * Apply the pending add/remove changes against the backend, then report the
   * outcome to the parent and close. Add and remove are independent calls; both
   * run when both kinds of change are pending.
   *
   * @returns A promise that resolves once the save attempt has completed.
   */
  const handleSave = async (): Promise<void> => {
    if (!hasChanges || saving) {
      return
    }

    setSaving(true)
    setSaveError(null)

    try {
      const token: string = await getAccessToken()

      // Each collection add/remove is a single-collection call carrying this
      // one source id. Run per-collection so one failure is isolated and
      // reported rather than silently masking the others.
      for (const collectionId of toAdd) {
        await addSourcesToCollection({
          idToken: token,
          collectionId,
          sourceIds: [sourceId],
        })
      }
      for (const collectionId of toRemove) {
        await removeSourcesFromCollection({
          idToken: token,
          collectionId,
          sourceIds: [sourceId],
        })
      }

      onChanged({
        sourceId,
        addedCollectionIds: toAdd,
        removedCollectionIds: toRemove,
      })
      onClose()
    } catch (err) {
      const message: string =
        err instanceof Error ? err.message : "Failed to update collections"
      setSaveError(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !saving) {
          onClose()
        }
      }}
    >
      <DialogContent className="sm:max-w-md" ref={dialogContentRef}>
        <DialogHeader>
          <DialogTitle>Manage Collections</DialogTitle>
          <DialogDescription>
            Choose which of your collections this article belongs to.
          </DialogDescription>
        </DialogHeader>

        {noOptions ? (
          <p className="text-sm text-muted-foreground">
            You have no collections to manage this article in.{" "}
            {onRequestCreateCollection ? (
              <button
                type="button"
                onClick={handleRequestCreateCollection}
                className="font-medium text-primary underline-offset-2 hover:underline"
              >
                Create a new collection
              </button>
            ) : (
              "Create a collection"
            )}{" "}
            or ask to be added to one.
          </p>
        ) : (
          <div className="space-y-2">
            <MultiSelectCombobox
              options={options}
              selected={selectedIds}
              onChange={setSelectedIds}
              placeholder="Select collections…"
              id="manage-collections"
              disabled={saving}
              listClassName="max-h-60"
              // Cap the selected-chips area so a large selection scrolls within
              // a fixed height rather than pushing the dialog taller.
              chipsClassName="max-h-24 overflow-y-auto items-start content-start"
              portalContainer={dialogContentRef}
            />
            {/* Offer the create flow here too, not just in the empty state. */}
            {onRequestCreateCollection && (
              <button
                type="button"
                onClick={handleRequestCreateCollection}
                disabled={saving}
                className="text-xs font-medium text-foreground hover:text-primary underline-offset-2 underline disabled:pointer-events-none disabled:opacity-50"
              >
                Or create a new collection
              </button>
            )}
          </div>
        )}

        {/* Inline error: shown only when a save attempt failed. */}
        {saveError !== null && (
          <p className="text-xs text-destructive" role="alert">
            {saveError}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || noOptions || !hasChanges}
          >
            {saving && <Loader2 className="size-3.5 animate-spin" />}
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
